"use client";

import { useEffect, useMemo, useState, type ComponentProps } from "react";

import Image from "next/image";

import {
  getProductImageOptimizerSrc,
  octogenImageLoader,
  shouldUseProductImageOptimizerLoader,
} from "@/lib/product-image-optimizer";

type OptimizedProductImageProps = Omit<ComponentProps<typeof Image>, "loader" | "src"> & {
  src: string;
};

/** Platform's optimizer-first/original-fallback policy for Cosimo thumbnails. */
export function OptimizedProductImage({
  src,
  onError,
  unoptimized,
  quality = 70,
  ...props
}: OptimizedProductImageProps) {
  const optimizedSrc = useMemo(() => getProductImageOptimizerSrc(src), [src]);
  const [currentSrc, setCurrentSrc] = useState(optimizedSrc);

  useEffect(() => {
    setCurrentSrc(optimizedSrc);
  }, [optimizedSrc]);

  const loader = shouldUseProductImageOptimizerLoader(currentSrc) ? octogenImageLoader : undefined;

  return (
    <Image
      {...props}
      key={currentSrc}
      src={currentSrc}
      quality={quality}
      loader={loader}
      unoptimized={loader ? false : (unoptimized ?? !currentSrc.startsWith("/"))}
      onError={(event) => {
        if (currentSrc !== src) {
          setCurrentSrc(src);
          return;
        }
        onError?.(event);
      }}
    />
  );
}
